import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { IUserAuth } from 'entities';
import { useInfiniteQuery, useQuery } from 'react-query';
import { ISpecialtyViewModel } from 'viewModels';
import SpecialtyController from '../controller/SpecialtyController';
import { TypeUser } from '../types/enums';
import { useAuth } from './auth';
import { CompanyController } from '../controller/CompanyController';

type ProviderProps = {
  children: ReactNode;
};

type SpecialtiesContextProps = {
  specialties: ISpecialtyViewModel[];
  setSpecialties: React.Dispatch<React.SetStateAction<ISpecialtyViewModel[]>>;
  specialtiesSelectedByUser: ISpecialtyViewModel[];
  setSpecialtiesSelectedByUser: React.Dispatch<React.SetStateAction<ISpecialtyViewModel[]>>;
  isLoadingSpecialties: boolean;
  hasNextPageSpecialty: boolean | undefined;
  isFetchingNextPageSpecialty: boolean | undefined;
  isLoadingSpecialtiesSelectedByUser: boolean;
  handleFetchNextPageSpecialty: () => void;
  hasNextPageSpecialtiesSelectedByUser: boolean | undefined;
  isFetchingNextPageSpecialtiesSelectedByUser: boolean | undefined;
};

const SpecialtiesContext = createContext<SpecialtiesContextProps>({} as SpecialtiesContextProps);

const SpecialtiesProvider = ({ children }: ProviderProps) => {
  const PAGE_SIZE = 50;
  const TIMER_TO_FETCH_NEXT_PAGE = 500;
  const { user, tokenIsValid, setUser } = useAuth();
  const [specialties, setSpecialties] = useState<ISpecialtyViewModel[]>([]);
  const [specialtiesSelectedByUser, setSpecialtiesSelectedByUser] = useState<ISpecialtyViewModel[]>(
    [] as ISpecialtyViewModel[],
  );

  const {
    isLoading: isLoadingSpecialties,
    fetchNextPage: fetchNextPageSpecialty,
    hasNextPage: hasNextPageSpecialty,
    isFetchingNextPage: isFetchingNextPageSpecialty,
  } = useInfiniteQuery(
    'specialties',
    ({ pageParam = 1 }) => SpecialtyController.getAll({ take: PAGE_SIZE, page: pageParam }),
    {
      onSuccess: (data) => {
        setSpecialties(data.pages?.flat().sort((a, b) => a.name.localeCompare(b.name)) ?? []);
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
      refetchOnWindowFocus: false,
      enabled: !!user.id && tokenIsValid,
    },
  );

  useQuery(
    'specialties-collaborator',
    () => CompanyController.getSpecialtiesByProfileId(user.collaboratorInfo?.profileId as string),
    {
      onSuccess: (res) => {
        setSpecialtiesSelectedByUser(res);
        setUser((prev) => {
          return {
            ...prev,
            collaboratorInfo: {
              ...prev.collaboratorInfo,
              profileInfo: {
                ...prev.collaboratorInfo?.profileInfo,
                specialtiesId: res.map((specialty) => specialty.id),
              },
            },
          } as IUserAuth;
        });
      },
      refetchOnWindowFocus: false,
      enabled:
        !!user.id &&
        !!user.collaboratorInfo?.profileId &&
        user.type === TypeUser.COLLABORATOR &&
        tokenIsValid,
    },
  );

  const {
    isLoading: isLoadingSpecialtiesSelectedByUser,
    fetchNextPage: fetchNextPageSpecialtiesSelectedByUser,
    hasNextPage: hasNextPageSpecialtiesSelectedByUser,
    isFetchingNextPage: isFetchingNextPageSpecialtiesSelectedByUser,
  } = useInfiniteQuery(
    'specialties-selected-by-user',
    ({ pageParam = 1 }) =>
      SpecialtyController.getSpecialtiesSelectedByUser({ take: PAGE_SIZE, page: pageParam }),
    {
      onSuccess: (res) => {
        setSpecialtiesSelectedByUser(res.pages?.flat() ?? []);
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
      refetchOnWindowFocus: false,
      enabled: !!user.id && tokenIsValid && user.type !== TypeUser.COLLABORATOR,
    },
  );

  const handleFetchNextPageSpecialty = useCallback(() => {
    fetchNextPageSpecialty();
    fetchNextPageSpecialtiesSelectedByUser();
  }, [fetchNextPageSpecialty, fetchNextPageSpecialtiesSelectedByUser]);

  useEffect(() => {
    if (!tokenIsValid) {
      setSpecialties([]);
      setSpecialtiesSelectedByUser([]);
    }
  }, [tokenIsValid]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (hasNextPageSpecialty) {
      interval = setInterval(() => {
        fetchNextPageSpecialty();
        fetchNextPageSpecialtiesSelectedByUser();
      }, TIMER_TO_FETCH_NEXT_PAGE);
    }
    return () => clearInterval(interval);
  }, [fetchNextPageSpecialtiesSelectedByUser, fetchNextPageSpecialty, hasNextPageSpecialty]);

  const contextValues = useMemo(
    () => ({
      specialties,
      setSpecialties,
      specialtiesSelectedByUser,
      setSpecialtiesSelectedByUser,
      isLoadingSpecialties,
      hasNextPageSpecialty,
      isFetchingNextPageSpecialty,
      isLoadingSpecialtiesSelectedByUser,
      handleFetchNextPageSpecialty,
      hasNextPageSpecialtiesSelectedByUser,
      isFetchingNextPageSpecialtiesSelectedByUser,
    }),
    [
      handleFetchNextPageSpecialty,
      hasNextPageSpecialtiesSelectedByUser,
      hasNextPageSpecialty,
      isFetchingNextPageSpecialtiesSelectedByUser,
      isFetchingNextPageSpecialty,
      isLoadingSpecialties,
      isLoadingSpecialtiesSelectedByUser,
      specialties,
      specialtiesSelectedByUser,
    ],
  );
  return (
    <SpecialtiesContext.Provider value={contextValues}>{children}</SpecialtiesContext.Provider>
  );
};

export default SpecialtiesProvider;

export const useSpecialties: () => SpecialtiesContextProps = () => useContext(SpecialtiesContext);
